import React, { useCallback, useEffect } from 'react';
import type Immutable from 'immutable';

import Tooltip from 'components/mui/Tooltip/TooltipForButton';
import IconButton from '@mui/material/IconButton';
import { Divider } from './StyledComponents';

import useBulkActions from './useBulkActions';

type BulkActionsProps = {
  paymentsIds: Immutable.Set<string>;
  clearSelection: () => void;
  disabled: boolean;
  selectAll: () => void;
};

const BulkActions: React.FC<BulkActionsProps> = ({ paymentsIds, clearSelection, disabled, selectAll }) => {
  const bulkMenuItems = useBulkActions({ paymentsIds, clearSelection });

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Cmd+A / Ctrl+A → Select all
      if (e.keyCode === 65 && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) selectAll();
      }

      // Esc → Clear selection
      if (e.keyCode === 27 && paymentsIds?.size) {
        clearSelection();
      }
    },
    [paymentsIds, clearSelection, disabled, selectAll],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, false);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <>
      {bulkMenuItems.map((item) => (
        <React.Fragment key={item.id}>
          <Tooltip
            t={{
              id: item.comingSoon ? 'contextMenu.comingSoon' : `company.paymentsWorkspace.bulkPanel.${item.id}`,
              defaultMessage: item.comingSoon ? 'Coming soon' : item.title,
            }}
            placement="top"
          >
            <IconButton
              aria-label={item.id}
              color={item.color ?? 'primary'}
              onClick={item.action}
              disabled={item.disabled || item.comingSoon}
            >
              <item.icon />
            </IconButton>
          </Tooltip>
          {item.separator && <Divider />}
        </React.Fragment>
      ))}
    </>
  );
};

export default BulkActions;
